import moment from 'moment'
import { ProductType } from 'sdk'
import { fetchGraphQLData } from 'services/query/fetchers/data-graph'
import { getLtDateQueryPart } from '../../utils'
import { UserPortfolioHistoryItem, parseUserIndexHistory } from './model'
import { Nullable } from 'utils'
import { Address } from 'viem'

export const getParamsForUserHistory =
  (userAddress: string, type: ProductType) => (date: number) => {
    switch (type) {
      case ProductType.INDEX_V1:
      case ProductType.INDEX_V2:
        return GET_USER_INDEX_HISTORY(userAddress, date)
      default:
        throw new Error(`Invalid product type: ${type}`)
    }
  }

export const GET_USER_INDEX_HISTORY = (user: string, dateLimit: number): string => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const date = getLtDateQueryPart(dateLimit)
  return `
  {
      accounts(where:{id: "${`${user}`}"}){
      id
      history(where:{${date}}, orderBy: timestamp, orderDirection: desc) {
        balance
        id
        index
        timestamp
      }
    }
  }
    `
}

export const getProductQueryFetcher = (type: ProductType) => {
  switch (type) {
    case ProductType.INDEX_V1:
    case ProductType.INDEX_V2:
      return fetchAllIndexHistory
    default:
      throw new Error(`Invalid product type: ${type}`)
  }
}
export type AccountRawResponse = {
  accounts: AccountHistoryRawResponse[]
}

export type AccountRaw = {
  balance: string
  index: string
  timestamp: string
}

export type AccountHistoryRawResponse = {
  id: string
  history: AccountRaw[]
}

export const fetchAllIndexHistory = async (
  url: string,
  queryParams: (now: number) => string,
  user: Address,
) => {
  return await fetchAllHistory<
    AccountRawResponse,
    Nullable<AccountHistoryRawResponse>,
    UserPortfolioHistoryItem | undefined
  >(url, queryParams, (res) => res.accounts[0], parseUserIndexHistory, user)
}

/**
 * Efficiently fetch the maximum number of items from the subgraph
 *
 * T is the wrapped payload eg "RawUserIndexHistoriesResponse"
 * K is the unwrapped payload eg "RawIndexHistory"
 * R is the parsed payload eg "UserHistory"
 *
 * @param url
 * @param queryParams
 * @param extractPayload
 * @param parseExtractedPayload
 * @returns
 */

export const fetchAllHistory = async <T, K, R>(
  url: string,
  queryParams: (now: number) => string,
  extractPayload: (response: Awaited<T>) => K,
  parseExtractedPayload: (response: K) => R[] | null,
  user: Address,
) => {
  // iterate through subgraph adding to the buffer
  let now = moment().unix()
  let buffer: Nullable<AccountRaw[]> = []
  let remaining: number = Number.MAX_VALUE

  while (remaining === 100 || remaining === Number.MAX_VALUE) {
    const res = await fetchGraphQLData<T>(url, queryParams(now))
    if (res) {
      const lastResponse = extractPayload(res) as AccountHistoryRawResponse

      if (lastResponse) {
        const { history } = lastResponse
        remaining = history.length

        if (history.length !== 0) {
          buffer = [...buffer, ...history]
          now = Number(history[history.length - 1].timestamp)
        }
      } else {
        remaining = 0
      }
    } else {
      buffer = null

      break
    }
  }

  return parseExtractedPayload({ id: user, history: buffer } as K)
}
